import { motion } from "framer-motion";
import { Plus, CheckCircle, XCircle, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Event {
  id: string;
  title: string;
  status: string;
  yes_pool: number;
  no_pool: number;
  total_bids: number;
  is_platform_event: boolean;
  deadline: string;
  created_at: string;
  outcome: boolean | null;
}

interface NewEventForm {
  title: string;
  description: string;
  category: string;
  country: string;
  deadline: string;
}

interface AdminEventsProps {
  events: Event[];
  isLoading: boolean;
  isCreating: boolean;
  resolvingEventId: string | null;
  deletingEventId: string | null;
  newEvent: NewEventForm;
  setNewEvent: (event: NewEventForm) => void;
  onCreateEvent: (e: React.FormEvent) => void;
  onResolve: (eventId: string, outcome: boolean) => void;
  onDelete: (eventId: string) => void;
}

const AdminEvents = ({
  events,
  isLoading,
  isCreating,
  resolvingEventId,
  deletingEventId,
  newEvent,
  setNewEvent,
  onCreateEvent,
  onResolve,
  onDelete,
}: AdminEventsProps) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Create Platform Event */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-6"
      >
        <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
          <Plus size={20} className="text-primary" />
          Create Platform Event
        </h2>

        <form onSubmit={onCreateEvent} className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input
              value={newEvent.title}
              onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
              required
            />
          </div>

          <div>
            <Label>Description</Label>
            <Textarea
              value={newEvent.description}
              onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Category</Label>
              <Select
                value={newEvent.category}
                onValueChange={(v) => setNewEvent({ ...newEvent, category: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sports">Sports</SelectItem>
                  <SelectItem value="crypto">Crypto</SelectItem>
                  <SelectItem value="politics">Politics</SelectItem>
                  <SelectItem value="tech">Tech</SelectItem>
                  <SelectItem value="entertainment">Entertainment</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Language</Label>
              <Select
                value={newEvent.country}
                onValueChange={(v) => setNewEvent({ ...newEvent, country: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">🌍 Global</SelectItem>
                  <SelectItem value="en">🇺🇸 English</SelectItem>
                  <SelectItem value="pt">🇧🇷 Portuguese</SelectItem>
                  <SelectItem value="es">🇪🇸 Spanish</SelectItem>
                  <SelectItem value="fr">🇫🇷 French</SelectItem>
                  <SelectItem value="de">🇩🇪 German</SelectItem>
                  <SelectItem value="it">🇮🇹 Italian</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Deadline</Label>
              <Input
                type="datetime-local"
                value={newEvent.deadline}
                onChange={(e) => setNewEvent({ ...newEvent, deadline: e.target.value })}
                required
              />
            </div>
          </div>

          <Button type="submit" variant="hero" className="w-full" disabled={isCreating}>
            {isCreating ? <Loader2 className="animate-spin" /> : "Create Platform Event"}
          </Button>
        </form>
      </motion.div>

      {/* Events List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="glass-card p-6"
      >
        <h2 className="text-xl font-bold text-foreground mb-4">
          Events ({events.length})
        </h2>

        <div className="space-y-3 max-h-[500px] overflow-y-auto">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : events.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No events yet</div>
          ) : (
            events.map((event) => (
              <div
                key={event.id}
                className="p-4 rounded-lg bg-muted/30 border border-border/50"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {event.is_platform_event && (
                        <span className="px-2 py-0.5 text-xs bg-primary/20 text-primary rounded">
                          PLATFORM
                        </span>
                      )}
                      <span
                        className={`px-2 py-0.5 text-xs rounded ${
                          event.status === "OPEN"
                            ? "bg-success/20 text-success"
                            : event.status === "CLOSED"
                            ? "bg-yellow-500/20 text-yellow-500"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {event.status}
                      </span>
                    </div>
                    <p className="font-medium text-foreground truncate">{event.title}</p>
                    <p className="text-sm text-muted-foreground">
                      Pool: {(event.yes_pool + event.no_pool).toLocaleString()} DC | {event.total_bids} predictions
                    </p>
                  </div>

                  <div className="flex gap-2">
                    {(event.status === "OPEN" || event.status === "CLOSED") && (
                      <>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => onResolve(event.id, true)}
                          disabled={resolvingEventId === event.id}
                          title="Resolve YES"
                        >
                          {resolvingEventId === event.id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <CheckCircle size={14} />
                          )}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => onResolve(event.id, false)}
                          disabled={resolvingEventId === event.id}
                          title="Resolve NO"
                        >
                          {resolvingEventId === event.id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <XCircle size={14} />
                          )}
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDelete(event.id)}
                      disabled={deletingEventId === event.id}
                      title="Delete event"
                      className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    >
                      {deletingEventId === event.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Trash2 size={14} />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AdminEvents;
